import { useId, useState } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

// ─── Tarjeta ────────────────────────────────────────────────────────────────

export interface CardProps {
  title?: ReactNode;
  help?: string;
  actions?: ReactNode;
  hint?: ReactNode;
  flush?: boolean;
  className?: string;
  children: ReactNode;
}

export function Card({
  title,
  help,
  actions,
  hint,
  flush,
  className,
  children,
}: CardProps): JSX.Element {
  return (
    <section className={`card${flush ? ' card--flush' : ''}${className ? ` ${className}` : ''}`}>
      {(title !== undefined || actions !== undefined) && (
        <header className={flush ? 'table-toolbar' : 'card__head'}>
          <div>
            {title !== undefined && (
              <h3 className="card__title">
                {title}
                {help !== undefined && <HelpTip text={help} />}
              </h3>
            )}
            {hint !== undefined && <p className="card__hint">{hint}</p>}
          </div>
          {actions !== undefined && <div className="inline">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

// ─── Botón ──────────────────────────────────────────────────────────────────

type ButtonVariant = 'default' | 'primary' | 'teal' | 'danger' | 'ghost' | 'icon';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  block?: boolean;
  loading?: boolean;
  icon?: IconName;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  default: '',
  primary: ' btn--primary',
  teal: ' btn--teal',
  danger: ' btn--danger',
  ghost: ' btn--ghost',
  icon: ' btn--icon',
};

export function Button({
  variant = 'default',
  size = 'md',
  block,
  loading,
  icon,
  children,
  className,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps): JSX.Element {
  return (
    <button
      type={type}
      className={`btn${VARIANT_CLASS[variant]}${size === 'sm' ? ' btn--sm' : ''}${
        block ? ' btn--block' : ''
      }${className ? ` ${className}` : ''}`}
      disabled={disabled === true || loading === true}
      aria-busy={loading === true}
      {...rest}
    >
      {loading === true ? (
        <span className="spinner" aria-hidden="true" />
      ) : (
        icon !== undefined && <Icon name={icon} />
      )}
      {children}
    </button>
  );
}

// ─── Insignia ───────────────────────────────────────────────────────────────

export type BadgeTone = 'green' | 'amber' | 'red' | 'neutral' | 'blue';

export function Badge({
  tone,
  children,
  dot,
  title,
}: {
  tone: BadgeTone;
  children: ReactNode;
  dot?: boolean;
  title?: string;
}): JSX.Element {
  return (
    <span className={`badge badge--${tone}`} title={title}>
      {dot === true && <span className="badge__dot" aria-hidden="true" />}
      {children}
    </span>
  );
}

// ─── Ayuda contextual ───────────────────────────────────────────────────────

export function HelpTip({ text }: { text: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <span className="help">
      <button
        type="button"
        className="help__btn"
        aria-label="Ayuda"
        aria-describedby={open ? id : undefined}
        aria-expanded={open}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen((current) => !current)}
      >
        i
      </button>
      {open && (
        <span className="help__bubble" role="tooltip" id={id}>
          {text}
        </span>
      )}
    </span>
  );
}

// ─── Esqueletos ─────────────────────────────────────────────────────────────

export function Skeleton({
  width = '100%',
  height = 14,
  radius,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number;
}): JSX.Element {
  return (
    <span
      className="skeleton"
      style={{ display: 'block', width, height, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

export function KpiSkeleton({ count = 6 }: { count?: number }): JSX.Element {
  return (
    <div className="grid-kpi" aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="kpi">
          <Skeleton width="65%" height={9} />
          <Skeleton width="45%" height={26} />
          <Skeleton width="80%" height={9} />
        </div>
      ))}
    </div>
  );
}

export function TableSkeleton({
  rows = 8,
  columns = 6,
}: {
  rows?: number;
  columns?: number;
}): JSX.Element {
  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 9 }} aria-hidden="true">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} style={{ display: 'flex', gap: 10 }}>
          {Array.from({ length: columns }, (_, colIndex) => (
            <Skeleton key={colIndex} height={12} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function ChartSkeleton({ height = 268 }: { height?: number }): JSX.Element {
  return <Skeleton height={height} radius={8} />;
}

// ─── Aviso genérico ─────────────────────────────────────────────────────────

export type NoticeTone = 'partial' | 'info' | 'error' | 'neutral';

const NOTICE_ICON: Record<NoticeTone, IconName> = {
  partial: 'alert',
  info: 'info',
  error: 'alert',
  neutral: 'info',
};

export function Notice({
  tone,
  children,
  role,
}: {
  tone: NoticeTone;
  children: ReactNode;
  role?: 'alert' | 'status';
}): JSX.Element {
  return (
    <div className={`notice notice--${tone}`} role={role}>
      <Icon name={NOTICE_ICON[tone]} size={15} />
      <div className="notice__body">{children}</div>
    </div>
  );
}
